'use client';

import { type FC, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BonusShopProductType } from '@/api/cms/bonusShop';
import { Button } from '@/components/buttons';
import { Countdown } from '@/components/Countdown';
import { ImageComponent } from '@/components/Image';
import { getTimeLeft } from '@/helpers/timer';
import { blurImagePlaceholder } from '@/theme/placeholders';
import { formatCompPoints } from '../../helpers';
import {
  useBonusShopProductExchange,
  useCanExchangeBonuses,
} from '../../hooks';
import type { BonusShopProduct } from '../../types';
import { BonusShopExchangeModal } from '../BonusShopExchangeModal';
import { BonusShopResultModal } from '../BonusShopResultModal';
import {
  ScBonusShopCard,
  ScBonusShopCardLabel,
  ScBonusShopCardTimer,
  ScBonusShopCardVersion,
  ScBonusShopCardVersions,
} from './styled';

interface BonusShopCardProps {
  product: BonusShopProduct;
}

const IMAGE_SIZE = 80;
const COIN_SIZE = 24;

export const BonusShopCard: FC<BonusShopCardProps> = memo(({ product }) => {
  const { t } = useTranslation(['bonusShop', 'common']);
  const [versionIndex, setVersionIndex] = useState(0);

  const canExchange = useCanExchangeBonuses();

  const activeIndex = Math.min(versionIndex, product.versions.length - 1);
  const version = product.versions[activeIndex];
  const title = version ? `${version.label} ${product.title}` : product.title;
  const subtitle = version ? version.subtitle : product.subtitle;
  const cpPrice = version ? version.cpPrice : product.cpPrice;
  const isPremium = product.type === BonusShopProductType.PREMIUM;

  const {
    balance,
    exchanged,
    isError,
    isExchangeOpen,
    isPending,
    isResultShown,
    openExchange,
    closeExchange,
    confirmExchange,
    closeResult,
    handleResultAction,
  } = useBonusShopProductExchange(product, version);

  const timeLeft = useMemo(
    () => getTimeLeft(product.durationTo),
    [product.durationTo]
  );

  return (
    <ScBonusShopCard $hasCta={canExchange} $type={product.type}>
      {product.label && (
        <ScBonusShopCardLabel $label={product.label}>
          {t(`labels.${product.label}`)}
        </ScBonusShopCardLabel>
      )}

      <div className="content">
        <div className="heading">
          <ImageComponent
            alt={product.title}
            blurDataURL={blurImagePlaceholder}
            className="image"
            height={IMAGE_SIZE}
            placeholder="blur"
            src={product.img?.url || blurImagePlaceholder}
            width={IMAGE_SIZE}
          />

          <div className="info">
            {isPremium && <p className="product-type">{t('premium')}</p>}
            <p className="title">
              {product.typeName ? (
                <>
                  {product.amount}{' '}
                  <span className="type-name">{product.typeName}</span>
                </>
              ) : (
                title
              )}
            </p>
            {subtitle && <p className="subtitle">{subtitle}</p>}
            {timeLeft > 0 && (
              <ScBonusShopCardTimer>
                <Countdown dual timeLeft={timeLeft}>
                  {({ days, hours, minutes, seconds }) => (
                    <>
                      <span>{`${days}${t('common:timerShort.d')}`}</span>
                      <span>{`${hours}${t('common:timerShort.h')}`}</span>
                      <span>{`${minutes}${t('common:timerShort.m')}`}</span>
                      <span>{`${seconds}${t('common:timerShort.s')}`}</span>
                    </>
                  )}
                </Countdown>
              </ScBonusShopCardTimer>
            )}
          </div>
        </div>

        <div className="divider" />

        {!!product.versions.length && (
          <ScBonusShopCardVersions>
            {product.versions.map((item, index) => (
              <ScBonusShopCardVersion
                $isActive={index === activeIndex}
                key={item.id}
                onClick={() => setVersionIndex(index)}
                type="button"
              >
                {item.label}
              </ScBonusShopCardVersion>
            ))}
          </ScBonusShopCardVersions>
        )}
      </div>

      <div className="footer">
        <span className="price">
          <ImageComponent
            alt="CP"
            height={COIN_SIZE}
            src="/common/cp-coin.webp"
            width={COIN_SIZE}
          />
          {formatCompPoints(cpPrice)}
        </span>
        {canExchange && (
          <div className="cta">
            <Button autoPaddingHorizontal onClick={openExchange} primary small>
              {t('buy')}
            </Button>
          </div>
        )}
      </div>

      {isExchangeOpen && (
        <BonusShopExchangeModal
          balance={balance}
          isPending={isPending}
          onClose={closeExchange}
          onConfirm={confirmExchange}
          product={product}
          version={version}
        />
      )}

      {isResultShown && exchanged && (
        <BonusShopResultModal
          balance={balance}
          isError={isError}
          onAction={handleResultAction}
          onClose={closeResult}
          product={exchanged.product}
          version={exchanged.version}
        />
      )}
    </ScBonusShopCard>
  );
});
